import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { useMetaForProject } from '@/layout/Seo/ProjectSeoHelper'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import {
  Download,
  getExhibitorDownloadsByProjectSlug,
  getExhibitorPageContentByProject,
} from '@/services/ExhibitorService/ExhibitorService'
import { getProjectAbout, ProjectMeta } from '@/services/ProjectsService'
import { EmbeddedVideo, Project } from '@/types/codegen-contentful'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'
import { ExhibitorsView } from '@/views/ExhibitorsView'

interface ExhibitorsProjectPageProps {
  locale: string
  assets: Download[]
  keyArt: Download[]
  keyImages: Download[]
  project: Project
  projectMeta: ProjectMeta
  trailers: Download[]
  videos: EmbeddedVideo[]
}

const ExhibitorsProjectPage: NextPageWithLayout<ExhibitorsProjectPageProps> = ({
  locale,
  assets,
  keyArt,
  keyImages,
  project,
  projectMeta,
  trailers,
  videos,
}) => {
  const meta = useMetaForProject({ ...projectMeta, locale })

  const url = useCanonicalUrl(`${paths.exhibitors.index}/${project.slug}`)
  const title = `${project.name} Exhibitors | Angel`
  const description = `${project.name} Exhibitor Kit`

  return (
    <>
      <Seo {...meta} canonical={url} title={title} description={description} />
      <ExhibitorsView
        assets={assets}
        keyArt={keyArt}
        keyImages={keyImages}
        project={project}
        trailers={trailers}
        videos={videos}
      />
    </>
  )
}

ExhibitorsProjectPage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<ExhibitorsProjectPageProps>(
  { filePath: 'pages/[locale]/exhibitors/[project].tsx' },
  async ({ federationClient, locale, next404, params, preview }) => {
    const projectSlug = getItemOrFirstEntry(params.project)
    if (!projectSlug) return next404()

    const [pageContent, downloads, projectMeta, translations] = await Promise.all([
      getExhibitorPageContentByProject(preview, projectSlug, locale),
      getExhibitorDownloadsByProjectSlug(preview, projectSlug, locale),
      getProjectAbout({ slug: projectSlug }, federationClient, { locale, preview }),
      loadTranslations(locale, ['common', 'account', 'watch', 'partnership']),
    ])

    const { downloadables, project, videos } = pageContent
    const { assets, trailers, keyArt, keyImages } = downloads
    if (!project.hasExhibitorPage || !projectMeta) return next404(Durations.FIVE_MINUTES_IN_SECONDS)

    return {
      props: {
        locale,
        assets,
        downloadables,
        keyArt,
        keyImages,
        project,
        projectMeta,
        trailers,
        videos,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default ExhibitorsProjectPage
